import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { forumAPI } from '../utils/api';
import { auth } from '../config/firebase';
import { io } from 'socket.io-client';

const socket = io(process.env.REACT_APP_SOCKET_URL, {
  autoConnect: false
});


const ForumDetail = () => {
  const { id: forumId } = useParams();
  const navigate = useNavigate();
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [lastSeen, setLastSeen] = useState(
    localStorage.getItem(`forum-last-seen-${forumId}`)
  );
  const MAX_TEXTAREA_HEIGHT = 100; // ~5 lines like WhatsApp

  
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(u => {
      setFirebaseUser(u);
    });
    return unsub;
  }, []);


  const [forum, setForum] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [socketReady, setSocketReady] = useState(false);
  const isMyMessage = (msg) =>
    msg.user === firebaseUser?.email;
  
  const isSystemMessage = (msg) =>
    msg.user === "System";
  
  const isBotMessage = (msg) =>
    msg.user?.toLowerCase().includes("bot");
  
  const getRepliedMessage = (replyToId) => {
    if (!replyToId) return null;
    return messages.find(m => m.id === replyToId);
  };
  
  const formatTime = (date) => {
    if (!date) return '--:--';
    return new Date(date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatDateLabel = (date) => {
    const d = new Date(date);
    const today = new Date();
  
    const isToday =
      d.toDateString() === today.toDateString();
  
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
  
    const isYesterday =
      d.toDateString() === yesterday.toDateString();
  
    if (isToday) return "Today";
    if (isYesterday) return "Yesterday";
  
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };
  const firstUnreadIndex = React.useMemo(() => {
    if (!lastSeen) return -1;
  
    const lastSeenDate = new Date(lastSeen);
  
    return messages.findIndex(
      msg => new Date(msg.createdAt) > lastSeenDate
    );
  }, [messages, lastSeen]);
  
  
  
  
  const bottomRef = useRef(null);

  /* ---------------- LOAD FORUM + HISTORY ---------------- */
  useEffect(() => {
    if (!firebaseUser || !forumId) return;
  
    let isMounted = true;
  
    const handleReceiveMessage = (incoming) => {
      setMessages(prev => {
        if (prev.some(m => m.id === incoming.id)) return prev;
        return [...prev, incoming];
      });
    };
  
    socket.on("receiveMessage", handleReceiveMessage);
  
    const loadForumAndMessages = async () => {
      try {
        setLoading(true);
  
        // 1️⃣ Load forum
        const forumData = await forumAPI.getById(forumId);
        if (!isMounted) return;
        setForum(forumData);
  
        // 2️⃣ Load history
        const posts = await forumAPI.getPosts(forumId);
        const history = posts.map(post => ({
          id: post.id,
          user: post.authorName || "Unknown",
          text: post.content,
          replyTo: post.parentPost || null,
          createdAt: post.createdAt
        }));
        setMessages(history);
  
        // 3️⃣ Ensure socket connection
        if (!socket.connected) {
          socket.connect();
        }
  
        // 4️⃣ Authenticate socket
        const token = await firebaseUser.getIdToken(true);
  
        socket.emit(
          "joinRoom",
          {
            roomPin: forumId,
            username: firebaseUser.email,
            token
          },
          (response) => {
            if (!isMounted) return;
  
            if (response?.success) {
              setSocketReady(true);
            } else {
              console.error("Join room failed:", response?.error);
              setError("Failed to authenticate chat");
            }
          }
        );
  
      } catch (err) {
        console.error(err);
        setError("Failed to load discussion");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
  
    loadForumAndMessages();
    
    return () => {
      isMounted = false;
      socket.off("receiveMessage", handleReceiveMessage);
    
      const now = new Date().toISOString();
      localStorage.setItem(`forum-last-seen-${forumId}`, now);
      setLastSeen(now);
    
      if (socket.connected) {
        socket.disconnect();
      }
    };
    
  }, [forumId, firebaseUser]);
  
  
  
  
  /* ---------------- AUTO SCROLL ---------------- */

  useEffect(() => {
    const container = bottomRef.current?.parentElement;
    if (!container) return;
  
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 120;
  
    if (isNearBottom) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);
  

  /* ---------------- SEND MESSAGE ---------------- */

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!socketReady) {
      console.warn("Socket not ready yet");
      return;
    }
    if (!socket.connected){
      console.error('Socket not connected');
      return;
    } 
    if (!message.trim()) return;

    socket.emit('sendMessage', {
      roomPin: forumId,              // forumId === roomPin
      text: message,
      replyTo: replyingTo?.id || null
    });
  
    setMessage('');
    setReplyingTo(null);
  };
  
  /* ---------------- UI STATES ---------------- */

  if (loading) {
    return <p style={{ padding: '2rem' }}>Loading discussion…</p>;
  }

  if (error) {
    return (
      <div style={{ padding: '2rem' }}>
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Go back</button>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '2rem',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <h1 style={{ marginBottom: '0.5rem' }}>{forum.title}</h1>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        {forum.description}
      </p>

      <div
        style={{
          border: "1px solid #e8e8e8",
          borderRadius: "12px",
          padding: "1rem",
          height: "50vh",              // 🔥 fixed height
          overflowY: "auto",           // 🔥 scroll inside
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginBottom: "1rem"
        }}
      >

        {messages
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          .map((msg, index) => {
            const currentDate = new Date(msg.createdAt).toDateString();
            const prevDate =
              index > 0
                ? new Date(messages[index - 1].createdAt).toDateString()
                : null;

            const showDateDivider = currentDate !== prevDate;

            return (
              <React.Fragment key={msg.id}>
                {/* 📅 DATE DIVIDER */}
                {showDateDivider && (
                  <div
                    style={{
                      alignSelf: "center",
                      fontSize: "0.85rem",
                      color: "#666",
                      margin: "1rem 0"
                    }}
                  >
                    {formatDateLabel(msg.createdAt)}
                  </div>
                )}

                {/* 🆕 NEW MESSAGES DIVIDER */}
                {index === firstUnreadIndex && (
                  <div
                    style={{
                      alignSelf: "center",
                      background: "#e0e0e0",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "12px",
                      fontSize: "0.8rem",
                      color: "#555"
                    }}
                  >
                    New messages
                  </div>
                )}

                {/* 💬 MESSAGE */}
                <div
                  onClick={() => {
                    if (!isSystemMessage(msg)) {
                      setReplyingTo(msg);
                    }
                  }}
                  style={{
                    alignSelf: isSystemMessage(msg)
                      ? "center"
                      : isMyMessage(msg)
                      ? "flex-end"
                      : "flex-start",
                    maxWidth: "70%",
                    cursor: isSystemMessage(msg) ? "default" : "pointer"
                  }}
                >

                  <div
                    style={{
                      background: isSystemMessage(msg)
                        ? "#f0f0f0"
                        : isBotMessage(msg)
                        ? "#e6e1ff"
                        : isMyMessage(msg)
                        ? "#1a1a1a"
                        : "#f5f5f5",
                      color: isMyMessage(msg) ? "#fff" : "#000",
                      padding: "0.6rem 0.8rem",
                      borderRadius: "12px",
                      fontSize: "0.9rem"
                    }}
                  >
                    {!isSystemMessage(msg) && (
                      <div
                        style={{
                          fontSize: "0.75rem",
                          opacity: 0.7,
                          marginBottom: "0.2rem"
                        }}
                      >
                        {msg.user}
                      </div>
                    )}
                    {msg.replyTo && (() => {
                      const repliedMsg = getRepliedMessage(msg.replyTo);
                      if (!repliedMsg) return null;

                      return (
                        <div
                          style={{
                            borderLeft: "3px solid #8b5cf6",
                            paddingLeft: "0.5rem",
                            marginBottom: "0.4rem",
                            fontSize: "0.75rem",
                            opacity: 0.85
                          }}
                        >
                          <div style={{ fontWeight: 600 }}>
                            {repliedMsg.user}
                          </div>
                          <div
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%"
                            }}
                          >
                            {repliedMsg.text}
                          </div>
                        </div>
                      );
                    })()}

                    <div>{msg.text}</div>

                    <div
                      style={{
                        textAlign: "right",
                        fontSize: "0.65rem",
                        marginTop: "0.25rem",
                        opacity: 0.6
                      }}
                    >
                      {formatTime(msg.createdAt)}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
      </div>


      <div>
        <form onSubmit={sendMessage} style={{ display: 'flex', gap: '0.5rem' }}>
        <div style={{width: '100%'}}>
          {replyingTo && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0.6rem 0.8rem",
                marginBottom: "0.5rem",
                background: "#f3f4f6",
                borderLeft: "4px solid #8b5cf6",
                borderRadius: "8px",
                fontSize: "0.85rem"
              }}
            >
              <div style={{ overflow: "hidden" }}>
                <div style={{ fontWeight: 600 }}>
                  Replying to {replyingTo.user}
                </div>
                <div
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    opacity: 0.7
                  }}
                >
                  {replyingTo.text}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setReplyingTo(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                  color: "#555"
                }}
              >
                ✕
              </button>
            </div>
          )}
            <div style={{ position: "relative", width: "100%" }}>
            <textarea
              value={message}
              placeholder="Type a message"
              rows={1}
              onChange={(e) => {
                setMessage(e.target.value);

                // auto-grow until max height
                e.target.style.height = "auto";
                e.target.style.height =
                  Math.min(e.target.scrollHeight, MAX_TEXTAREA_HEIGHT) + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              style={{
                width: "100%",
                resize: "none",
                overflowY: "auto",
                padding: "0.65rem 3rem 0.65rem 0.9rem",
                borderRadius: "10px",
                border: "1px solid #e8e8e8",
                fontSize: "0.95rem",
                lineHeight: "1.4",
                outline: "none",
                maxHeight: `${MAX_TEXTAREA_HEIGHT}px`,
                minHeight: "40px", // pill height
              }}
            />
              <button
                type="submit"
                disabled={!message.trim()}
                style={{
                  position: "absolute",
                  right: "0.6rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: message.trim() ? "pointer" : "default",
                  opacity: message.trim() ? 1 : 0.4,
                  fontSize: "1.2rem"
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 20L21 12L3 4V10L17 12L3 14V20Z"
                    fill={message.trim() ? "#1a1a1a" : "#999"}
                  />
                </svg>

              </button>
            </div>
        </div>
        </form>
      </div>
    </div>
  );
};

export default ForumDetail;
